import { useState, useEffect } from 'react';
import { API, useAuth } from '@/App';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import {
  Crown, Users, DollarSign, TrendingUp, Award, Shield,
  AlertTriangle, CheckCircle2, Percent, RefreshCw, UserPlus,
  ChevronDown, ChevronUp, Banknote, Wallet, ArrowRight
} from 'lucide-react';

const SuperSoldierPayoutDashboard = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [superSoldiers, setSuperSoldiers] = useState([]);
  const [selectedSuperSoldier, setSelectedSuperSoldier] = useState(null);
  const [overrideDetails, setOverrideDetails] = useState(null);
  const [processingPayout, setProcessingPayout] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [soldiers, setSoldiers] = useState([]);
  const [assignForm, setAssignForm] = useState({ soldier_id: '', super_soldier_id: '' });

  useEffect(() => {
    fetchSuperSoldiers();
    fetchAllSoldiers();
  }, []);

  const fetchSuperSoldiers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/team/super-soldiers/with-platoons`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuperSoldiers(response.data.super_soldiers || []);
    } catch (error) {
      console.error('Failed to fetch super soldiers:', error);
      toast.error('Failed to load Super Soldiers');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllSoldiers = async () => {
    try {
      const response = await axios.get(`${API}/admin/team/super-soldier/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSoldiers(response.data.soldiers || []);
    } catch (error) {
      console.error('Failed to fetch soldiers:', error);
    }
  };

  const fetchOverrideDetails = async (superSoldierId) => {
    try {
      const response = await axios.get(`${API}/admin/team/super-soldier/${superSoldierId}/override`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOverrideDetails(response.data);
      setSelectedSuperSoldier(superSoldierId);
    } catch (error) {
      console.error('Failed to fetch override details:', error);
      toast.error('Failed to load override details');
    }
  };

  const handleProcessPayout = async (superSoldierId) => {
    setProcessingPayout(true);
    try {
      const response = await axios.post(`${API}/admin/team/super-soldier/${superSoldierId}/payout`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Payout processed: $${response.data.amount}`);
      fetchSuperSoldiers();
      if (selectedSuperSoldier === superSoldierId) {
        fetchOverrideDetails(superSoldierId);
      }
    } catch (error) {
      console.error('Payout failed:', error);
      toast.error(error.response?.data?.detail || 'Payout failed');
    } finally {
      setProcessingPayout(false);
    }
  };

  const handleAssignToPlatoon = async () => {
    try {
      await axios.post(`${API}/admin/team/platoon/assign`, assignForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Soldier assigned to platoon');
      setShowAssignModal(false);
      setAssignForm({ soldier_id: '', super_soldier_id: '' });
      fetchSuperSoldiers();
    } catch (error) {
      console.error('Assignment failed:', error);
      toast.error(error.response?.data?.detail || 'Assignment failed');
    }
  };

  const handleRemoveFromPlatoon = async (soldierId) => {
    try {
      await axios.post(`${API}/admin/team/platoon/remove`, { soldier_id: soldierId }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Soldier removed from platoon');
      fetchSuperSoldiers();
    } catch (error) {
      console.error('Removal failed:', error);
      toast.error(error.response?.data?.detail || 'Removal failed');
    }
  };

  // Calculate totals
  const totalPendingOverride = superSoldiers.reduce((sum, ss) => sum + (ss.pending_override || 0), 0);
  const totalPaidOverride = superSoldiers.reduce((sum, ss) => sum + (ss.total_override_earned || 0), 0);

  return (
    <div className="space-y-6" data-testid="super-soldier-payout-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
            <Crown className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-white">Super Soldier Payouts</h2>
            <p className="text-zinc-400">3% Management Override Dashboard</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={fetchSuperSoldiers}
            className="border-zinc-700 text-zinc-300"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button
            onClick={() => setShowAssignModal(true)}
            className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Assign to Platoon
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <Crown className="w-5 h-5 text-purple-400" />
              <Badge variant="outline" className="text-purple-400 border-purple-500/30">
                {superSoldiers.length}/4
              </Badge>
            </div>
            <div className="text-2xl font-bold text-white">{superSoldiers.length}</div>
            <div className="text-sm text-zinc-400">Super Soldiers</div>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <Users className="w-5 h-5 text-blue-400" />
            </div>
            <div className="text-2xl font-bold text-white">
              {superSoldiers.reduce((sum, ss) => sum + (ss.platoon_size || 0), 0)}
            </div>
            <div className="text-sm text-zinc-400">Total Platoon Members</div>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <Wallet className="w-5 h-5 text-amber-400" />
              <Badge variant="outline" className="text-amber-400 border-amber-500/30">Pending</Badge>
            </div>
            <div className="text-2xl font-bold text-amber-400">${totalPendingOverride.toFixed(2)}</div>
            <div className="text-sm text-zinc-400">Pending Override</div>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <Banknote className="w-5 h-5 text-green-400" />
              <Badge variant="outline" className="text-green-400 border-green-500/30">Paid</Badge>
            </div>
            <div className="text-2xl font-bold text-green-400">${totalPaidOverride.toFixed(2)}</div>
            <div className="text-sm text-zinc-400">Total Paid Out</div>
          </CardContent>
        </Card>
      </div>

      {/* Super Soldiers List */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Shield className="w-5 h-5 text-purple-400" />
            Super Soldier Override Earnings
          </CardTitle>
          <CardDescription className="text-zinc-400">
            3% lifetime management override on all platoon earnings
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-zinc-500">Loading...</div>
          ) : superSoldiers.length === 0 ? (
            <div className="text-center py-8 text-zinc-500">
              No Super Soldiers assigned yet. Use the "Assign to Platoon" button to get started.
            </div>
          ) : (
            <div className="space-y-4">
              {superSoldiers.map((ss) => (
                <Card key={ss.super_soldier_id} className="bg-zinc-800/50 border-zinc-700">
                  <CardContent className="p-4">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                      {/* Info */}
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                          <Crown className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-white">{ss.name}</h4>
                          <p className="text-sm text-zinc-400">{ss.email}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="outline" className="text-purple-400 border-purple-500/30 text-xs">
                              Platoon: {ss.platoon_size}/25
                            </Badge>
                            <Badge variant="outline" className="text-amber-400 border-amber-500/30 text-xs">
                              3% Override
                            </Badge>
                          </div>
                        </div>
                      </div>

                      {/* Stats */}
                      <div className="flex flex-wrap items-center gap-4">
                        <div className="text-center px-4 py-2 rounded-lg bg-zinc-900/50">
                          <div className="text-lg font-bold text-amber-400">${(ss.pending_override || 0).toFixed(2)}</div>
                          <div className="text-xs text-zinc-500">Pending</div>
                        </div>
                        <div className="text-center px-4 py-2 rounded-lg bg-zinc-900/50">
                          <div className="text-lg font-bold text-green-400">${(ss.total_override_earned || 0).toFixed(2)}</div>
                          <div className="text-xs text-zinc-500">Paid Out</div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => fetchOverrideDetails(ss.super_soldier_id)}
                            className="border-purple-500/30 text-purple-400 hover:bg-purple-500/10"
                          >
                            View Details
                          </Button>
                          {(ss.pending_override || 0) > 0 && (
                            <Button
                              size="sm"
                              onClick={() => handleProcessPayout(ss.super_soldier_id)}
                              disabled={processingPayout}
                              className="bg-green-600 hover:bg-green-700 text-white"
                            >
                              {processingPayout ? 'Processing...' : 'Pay Now'}
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Expanded Details */}
                    {selectedSuperSoldier === ss.super_soldier_id && overrideDetails && (
                      <div className="mt-4 pt-4 border-t border-zinc-700">
                        <h5 className="font-medium text-white mb-3">Override Breakdown</h5>
                        <div className="grid md:grid-cols-3 gap-4 mb-4">
                          <div className="p-3 rounded-lg bg-zinc-900/50">
                            <div className="text-sm text-zinc-400">Total Platoon Earnings</div>
                            <div className="text-xl font-bold text-white">${overrideDetails.total_platoon_earnings?.toFixed(2)}</div>
                          </div>
                          <div className="p-3 rounded-lg bg-zinc-900/50">
                            <div className="text-sm text-zinc-400">Monthly Recurring</div>
                            <div className="text-xl font-bold text-blue-400">${overrideDetails.monthly_recurring_override?.toFixed(2)}/mo</div>
                          </div>
                          <div className="p-3 rounded-lg bg-zinc-900/50">
                            <div className="text-sm text-zinc-400">Total Override (3%)</div>
                            <div className="text-xl font-bold text-purple-400">${overrideDetails.total_override?.toFixed(2)}</div>
                          </div>
                        </div>

                        {/* Projections */}
                        <h5 className="font-medium text-white mb-3">3-Year Projections</h5>
                        <div className="grid md:grid-cols-3 gap-3">
                          <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
                            <div className="text-xs text-blue-400">Year 1 (12,500 seats)</div>
                            <div className="text-lg font-bold text-white">$11,250 - $18,750/mo</div>
                          </div>
                          <div className="p-3 rounded-lg bg-purple-500/10 border border-purple-500/20">
                            <div className="text-xs text-purple-400">Year 2 (25,000 seats)</div>
                            <div className="text-lg font-bold text-white">$22,500 - $37,500/mo</div>
                          </div>
                          <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                            <div className="text-xs text-amber-400">Year 3 (37,500 seats)</div>
                            <div className="text-lg font-bold text-white">$33,750 - $56,250/mo</div>
                          </div>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Assign to Platoon Modal */}
      {showAssignModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <Card className="w-full max-w-md bg-zinc-900 border-zinc-700">
            <CardHeader className="border-b border-zinc-800">
              <div className="flex items-center justify-between">
                <CardTitle className="text-white flex items-center gap-2">
                  <UserPlus className="w-5 h-5 text-purple-400" />
                  Assign Soldier to Platoon
                </CardTitle>
                <button
                  onClick={() => setShowAssignModal(false)}
                  className="text-zinc-400 hover:text-white p-2"
                >
                  ✕
                </button>
              </div>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">
                  Select Super Soldier
                </label>
                <select
                  value={assignForm.super_soldier_id}
                  onChange={(e) => setAssignForm(prev => ({ ...prev, super_soldier_id: e.target.value }))}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-md px-3 py-2 text-white"
                >
                  <option value="">Choose Super Soldier...</option>
                  {superSoldiers.map(ss => (
                    <option key={ss.super_soldier_id} value={ss.super_soldier_id}>
                      {ss.name} ({ss.platoon_size}/25)
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">
                  Select Soldier
                </label>
                <select
                  value={assignForm.soldier_id}
                  onChange={(e) => setAssignForm(prev => ({ ...prev, soldier_id: e.target.value }))}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-md px-3 py-2 text-white"
                >
                  <option value="">Choose Soldier...</option>
                  {soldiers.map(s => (
                    <option key={s.soldier_id} value={s.soldier_id}>
                      {s.name} ({s.email})
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setShowAssignModal(false)}
                  className="flex-1 border-zinc-700"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleAssignToPlatoon}
                  disabled={!assignForm.soldier_id || !assignForm.super_soldier_id}
                  className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white"
                >
                  Assign to Platoon
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default SuperSoldierPayoutDashboard;
